/**
 * Attach horizontal touch-swipe navigation to a carousel/slider root.
 *
 * Uses passive touch listeners and never calls preventDefault, so vertical page
 * scrolling is untouched — a gesture only counts as a swipe when the horizontal
 * movement clears `threshold` AND dominates the vertical movement (so a diagonal
 * scroll won't fire it). A plain tap (no movement) is ignored, leaving taps on
 * links/buttons inside the carousel working normally.
 *
 * Swipe left → onNext (content advances), swipe right → onPrev.
 */
export interface SwipeHandlers {
    onNext: () => void;
    onPrev: () => void;
}

export function addSwipe(
    el: HTMLElement,
    { onNext, onPrev }: SwipeHandlers,
    threshold = 45
): void {
    let startX = 0;
    let startY = 0;
    let tracking = false;

    el.addEventListener('touchstart', (event) => {
        // Only track single-finger gestures; ignore pinch/multi-touch.
        if (event.touches.length !== 1) {
            tracking = false;
            return;
        }
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        tracking = true;
    }, { passive: true });

    el.addEventListener('touchend', (event) => {
        if (!tracking) return;
        tracking = false;

        const touch = event.changedTouches[0];
        if (!touch) return;

        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;

        // Require a dominant horizontal movement past the threshold; otherwise
        // it's a tap or a vertical scroll and we leave it alone.
        if (Math.abs(dx) < threshold || Math.abs(dx) <= Math.abs(dy)) return;

        if (dx < 0) {
            onNext();
        } else {
            onPrev();
        }
    }, { passive: true });

    el.addEventListener('touchcancel', () => { tracking = false; }, { passive: true });
}
